import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import "line-awesome/dist/line-awesome/css/line-awesome.min.css";
import { Helmet } from "react-helmet";
import api from "../../services/api";

// Group display order (adjust strings to match your real plan_type values)
const GROUP_ORDER = ["3rd Party Services", "Partner", "Employee", "Business"];

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------------------------------------------------
  // Fetch Plans
  // ---------------------------------------------------
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await api.get("/plans");

        const planData = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        setPlans(planData);
      } catch (err) {
        console.error("Failed to load plans:", err);
        setError("Failed to load plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // ---------------------------------------------------
  // Group Plans By Type + sort each group
  // (sort_order first if API sends it, then price ascending)
  // so index 0..3 => Basic, Smart, Growth, Premium
  // => green, blue, purple, orange in every group
  // ---------------------------------------------------
  const groupedPlans = useMemo(() => {
    const groups = plans.reduce((acc, plan) => {
      const type = plan.plan_type || "Plans";
      (acc[type] = acc[type] || []).push(plan);
      return acc;
    }, {});

    Object.keys(groups).forEach((type) => {
      groups[type].sort(
        (a, b) =>
          (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
          Number(a.price) - Number(b.price)
      );
    });

    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => {
        const ia = GROUP_ORDER.indexOf(a);
        const ib = GROUP_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
    );
  }, [plans]);

  // ---------------------------------------------------
  // Helpers
  // ---------------------------------------------------
  const getFeatures = (plan) => {
    if (Array.isArray(plan.features)) return plan.features;

    if (typeof plan.features === "string") {
      return plan.features
        .split(",")
        .map((feature) => feature.trim())
        .filter(Boolean);
    }

    return [];
  };

  const getDuration = (plan) => {
    const days =
      plan.validity_days ??
      plan.duration_days ??
      plan.validity ??
      plan.duration;

    if (!days) return null;

    // If API already sends something like "30 Days"
    if (typeof days === "string" && isNaN(Number(days))) return days;

    const numericDays = Number(days);
    if (!numericDays) return null;

    return `${numericDays} ${numericDays === 1 ? "Day" : "Days"}`;
  };

  const getMonthlyPrice = (plan) => {
    if (
      plan.monthly_price !== undefined &&
      plan.monthly_price !== null &&
      plan.monthly_price !== ""
    ) {
      return Number(plan.monthly_price).toFixed(0);
    }

    const price = Number(plan.price);
    const duration =
      Number(plan.validity_days) ||
      Number(plan.duration_days) ||
      Number(plan.validity) ||
      Number(plan.duration);

    if (!price || !duration) return null;

    return Math.round(price / (duration / 30));
  };

  const getPlanIcon = (plan, index) => {
    if (plan.icon) return plan.icon;

    const defaultIcons = [
      "la la-paper-plane",
      "la la-rocket",
      "la la-crown",
      "la la-gem",
      "la la-star",
    ];

    return defaultIcons[index % defaultIcons.length];
  };

  const getPlanColor = (plan, index) => {
    if (plan.color) return plan.color;

    const defaultColors = [
      "#159957", // Basic   - green
      "#0d5be1", // Smart   - blue
      "#7627c9", // Growth  - purple
      "#e67e22", // Premium - orange
      "#d63384",
    ];

    return defaultColors[index % defaultColors.length];
  };

  const isPopular = (plan) =>
    plan.is_popular === true ||
    plan.is_popular === 1 ||
    plan.popular === true ||
    plan.popular === 1;

  const isBestValue = (plan) =>
    plan.is_best_value === true ||
    plan.is_best_value === 1 ||
    plan.best_value === true ||
    plan.best_value === 1;

  const isActive = (plan) => {
    if (!plan.status) return true;
    return String(plan.status).toLowerCase() === "active";
  };

  const getButtonText = (plan) =>
    !isActive(plan) ? "Unavailable" : plan.button_text || "BUY NOW";

  const getButtonUrl = (plan) =>
    plan.purchase_url || plan.button_url || plan.url || "/register";

  // ---------------------------------------------------
  // Loading
  // ---------------------------------------------------
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="pricing-loading">
          <div className="pricing-spinner"></div>
          <h4>Loading Plans...</h4>
        </div>
        <Footer />
      </>
    );
  }

  // ---------------------------------------------------
  // Error
  // ---------------------------------------------------
  if (error) {
    return (
      <>
        <Navbar />
        <div className="pricing-error">
          <i className="la la-exclamation-circle"></i>
          <h3>Error Loading Plans</h3>
          <p>{error}</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Our Plans - BusinessMate Workflow</title>
        <meta
          name="description"
          content="Choose the best plan for your business."
        />
      </Helmet>

      <Navbar />

      {/* ================= BREADCRUMB / HERO ================= */}
      <section
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          padding: "40px 0",
          color: "#fff",
        }}
      >
        <div className="container text-center">
          <h1
            style={{
              fontSize: 42,
              fontWeight: 700,
              marginBottom: 18,
              color: "#fff",
            }}
          >
            Plans & Pricing
          </h1>

          <p
            style={{
              fontSize: 19,
              opacity: 0.9,
              maxWidth: 850,
              margin: "0 auto 15px",
              lineHeight: 1.7,
            }}
          >
            Explore flexible plans, clear pricing, and tailored options
            designed to help you choose the right plan, maximize value, and get
            the most from our services.
          </p>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section className="dynamic-pricing-section">
        <div className="container">
          <div className="pricing-heading">
            <h1>Choose the Best Plan for You</h1>
          </div>

          {Object.entries(groupedPlans).map(([planType, planList]) => (
            <div className="plan-type-wrapper" key={planType}>
              {/* Show category only when there are multiple groups */}
              {Object.keys(groupedPlans).length > 1 && (
                <div className="plan-category-title">
                  <h3>
                    {planType === "Business"
                      ? "Business for Sell"
                      : planType === "Employee"
                      ? "Skilled Business Employee"
                      : planType}
                  </h3>
                </div>
              )}

              <div className="pricing-grid">
                {planList.map((plan, index) => {
                  const features = getFeatures(plan);
                  const color = getPlanColor(plan, index);
                  const monthlyPrice = getMonthlyPrice(plan);
                  const duration = getDuration(plan);
                  const popular = isPopular(plan);
                  const bestValue = isBestValue(plan);
                  const active = isActive(plan);

                  // Show on every plan (including 1-month plans where
                  // price === monthlyPrice) unless the API sends its own savings text
                  const showCalculatedSaving = !plan.savings;

                  return (
                    <div
                      className={`pricing-card-wrapper ${
                        popular ? "popular-card" : ""
                      } ${bestValue ? "best-value-card" : ""}`}
                      key={plan.id || `${planType}-${index}`}
                    >
                      {/* MOST POPULAR BADGE */}
                      {popular && (
                        <div className="popular-badge">
                          <i className="la la-star"></i>
                          {plan.badge || "MOST POPULAR"}
                        </div>
                      )}

                      {/* BEST VALUE BADGE */}
                      {bestValue && (
                        <div className="best-value-badge">
                          <i className="la la-trophy"></i>
                          <span>{plan.badge || "BEST VALUE"}</span>
                        </div>
                      )}

                      <div
                        className={`dynamic-pricing-card ${
                          !active ? "inactive-plan" : ""
                        }`}
                        style={{ "--plan-color": color }}
                      >
                        {/* TOP COLOR BAR */}
                        <div
                          className="pricing-color-bar"
                          style={{ backgroundColor: color }}
                        ></div>

                        {/* PLAN HEADER */}
                        <div className="pricing-card-header">
                          <div
                            className="plan-icon-wrapper"
                            style={{ backgroundColor: `${color}12` }}
                          >
                            <i
                              className={getPlanIcon(plan, index)}
                              style={{ color }}
                            ></i>
                          </div>

                          <h2>{plan.plan_name || "Plan"}</h2>

                          <p className="plan-tagline">
                            {plan.tagline || "Perfect for your business"}
                          </p>
                        </div>

                        <div className="pricing-divider"></div>

                        {/* PRICE */}
                        <div className="pricing-price">
                          <div className="main-price">
                            <span className="currency">₹</span>
                            <span className="price-number">
                              {Number(plan.price || 0).toFixed(0)}
                            </span>
                          </div>

                          {monthlyPrice && (
                            <div className="monthly-price" style={{ color }}>
                              Only ₹{monthlyPrice} / Month
                            </div>
                          )}
                        </div>

                        <div className="pricing-divider"></div>

                        {/* FEATURES */}
                        <div className="pricing-features">
                          {duration && (
                            <div className="pricing-feature">
                              <i
                                className="la la-check-circle"
                                style={{ color }}
                              ></i>
                              <span>Valid for {duration}</span>
                            </div>
                          )}

                          {features.map((feature, featureIndex) => (
                            <div className="pricing-feature" key={featureIndex}>
                              <i
                                className="la la-check-circle"
                                style={{ color }}
                              ></i>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        {/* EXTRA (content + saving) - fixed-size slot */}
                        <div className="pricing-extra">
                          {plan.content && (
                            <div className="pricing-content">
                              {plan.content}
                            </div>
                          )}

                          {plan.savings && (
                            <div
                              className="saving-box"
                              style={{
                                backgroundColor: `${color}10`,
                                color,
                              }}
                            >
                              <i className="la la-tag"></i>
                              <span>{plan.savings}</span>
                            </div>
                          )}

                          {showCalculatedSaving && (
                            <div
                              className="saving-box"
                              style={{
                                backgroundColor: `${color}10`,
                                color,
                              }}
                            >
                              <i className="la la-tag"></i>
                              <span>Best value compared to monthly</span>
                            </div>
                          )}
                        </div>

                        {/* BUTTON */}
                        <div className="pricing-button-wrapper">
                          <a
                            href={active ? getButtonUrl(plan) : "#"}
                            className={`pricing-buy-btn ${
                              !active ? "disabled" : ""
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={(e) => {
                              if (!active) e.preventDefault();
                            }}
                          >
                            <span>{getButtonText(plan)}</span>
                            <i className="la la-angle-right"></i>
                          </a>
                        </div>

                        {!active && (
                          <div className="inactive-label">
                            This plan is currently unavailable
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* NO PLANS */}
          {plans.length === 0 && (
            <div className="no-plans">
              <i className="la la-box-open"></i>
              <h4>No Plans Available</h4>
              <p>Currently there are no pricing plans available.</p>
            </div>
          )}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="pricing-cta">
        <div className="container">
          <div className="pricing-cta-content">
            <h2>Need a custom solution for your business?</h2>

            <a href="/contact" className="pricing-cta-button">
              Contact Us
              <i className="la la-angle-right"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* ================= CSS ================= */}
      <style>{`
  /* MAIN */
  .dynamic-pricing-section {
    padding: 40px 0 65px;
    background: #ffffff;
  }

  .pricing-heading {
    text-align: center;
    margin-bottom: 40px;
  }

  .pricing-heading h1 {
    margin: 0;
    font-size: 38px;
    line-height: 1.2;
    font-weight: 800;
    color: #10254b;
  }

  .pricing-heading p {
    margin: 8px 0 0;
    font-size: 18px;
    color: #4b5563;
  }

  /* CATEGORY */
  .plan-type-wrapper {
    margin-bottom: 45px;
  }

  .plan-category-title {
    text-align: center;
    margin-bottom: 25px;
  }

  .plan-category-title h3 {
    display: inline-block;
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    border-bottom: 2px solid #e5e7eb;
    padding: 0 20px 10px;
  }

  /* GRID - 4 COLUMNS, equal row height */
  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    gap: 24px;
    align-items: stretch;
  }

  .pricing-card-wrapper {
    position: relative;
    min-width: 0;
  }

  /* CARD */
  .dynamic-pricing-card {
    position: relative;
    height: 100%;
    min-height: 500px;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.07);
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
  }

  .dynamic-pricing-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 35px rgba(0, 0, 0, 0.12);
  }

  .pricing-card-wrapper.popular-card .dynamic-pricing-card,
  .pricing-card-wrapper.best-value-card .dynamic-pricing-card {
    border: 2px solid var(--plan-color);
  }

  /* COLOR BAR */
  .pricing-color-bar {
    height: 42px;
    width: 100%;
  }

  /* BADGES */
  .popular-badge {
    position: absolute;
    z-index: 5;
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    background: #f28b00;
    color: white;
    padding: 9px 18px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.14);
  }

  .popular-badge i {
    margin-right: 4px;
  }

  .best-value-badge {
    position: absolute;
    z-index: 5;
    top: -2px;
    right: 14px;
    width: 68px;
    min-height: 92px;
    background: #f8d34f;
    color: #684800;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 10px;
    font-weight: 800;
    padding: 8px 4px 15px;
    clip-path: polygon(0 0, 100% 0, 100% 88%, 50% 100%, 0 88%);
  }

  .best-value-badge i {
    font-size: 21px;
    margin-bottom: 4px;
  }

  /* HEADER - same height on every card */
  .pricing-card-header {
    text-align: center;
    padding: 14px 18px 5px;
    min-height: 175px;
  }

  .plan-icon-wrapper {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px auto 10px;
  }

  .plan-icon-wrapper i {
    font-size: 36px;
  }

  .pricing-card-header h2 {
    font-size: 24px;
    line-height: 1.2;
    font-weight: 800;
    margin: 0 0 5px;
    color: var(--plan-color);
  }

  .plan-tagline {
    margin: 0;
    min-height: 20px;
    font-size: 14px;
    line-height: 1.4;
    color: #222;
  }

  /* DIVIDER */
  .pricing-divider {
    height: 1px;
    background: #dedede;
    margin: 15px 18px 0;
  }

  /* PRICE - same height on every card */
  .pricing-price {
    text-align: center;
    padding: 12px 15px 0;
    min-height: 100px;
  }

  .main-price {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    color: var(--plan-color);
    line-height: 1;
  }

  .currency {
    color: #222;
    font-size: 18px;
    font-weight: 600;
    margin-top: 6px;
    margin-right: 4px;
  }

  .price-number {
    font-size: 48px;
    font-weight: 800;
    letter-spacing: -1px;
  }

  .monthly-price {
    display: inline-block;
    margin-top: 10px;
    padding: 6px 15px;
    border-radius: 18px;
    background: #f4f5f7;
    font-size: 14px;
    font-weight: 700;
  }

  /* FEATURES - grows, pushes extras + button to bottom */
  .pricing-features {
    padding: 14px 20px 5px;
    flex: 1;
    min-height: 70px;
  }

  .pricing-feature {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 9px;
    font-size: 13px;
    color: #202020;
    line-height: 1.35;
  }

  .pricing-feature i {
    flex-shrink: 0;
    font-size: 17px;
    margin-top: -1px;
  }

  /* EXTRA SLOT (content + saving) - reserved even when empty.
     Raise min-height if your content text is long. */
  .pricing-extra {
    min-height: 96px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .pricing-content {
    padding: 0 20px 4px;
    font-size: 12px;
    color: #666;
    line-height: 1.4;
    text-align: center;
  }

  .saving-box {
    margin: 8px 20px 12px;
    padding: 8px 10px;
    border-radius: 6px;
    text-align: center;
    font-size: 12px;
    line-height: 1.3;
    font-weight: 600;
  }

  .saving-box i {
    margin-right: 4px;
  }

  /* BUTTON */
  .pricing-button-wrapper {
    padding: 0 20px 20px;
    margin-top: auto;
  }

  .pricing-buy-btn {
    width: 100%;
    min-height: 52px;
    border-radius: 7px;
    color: white !important;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    text-decoration: none !important;
    font-size: 15px;
    font-weight: 700;
    transition: all 0.3s ease;
  }

  .pricing-buy-btn:hover {
    opacity: 0.88;
    transform: translateY(-2px);
    color: white !important;
  }

  .pricing-buy-btn i {
    font-size: 18px;
  }

  .pricing-buy-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .inactive-plan {
    opacity: 0.75;
  }

  .inactive-label {
    text-align: center;
    font-size: 11px;
    color: #888;
    padding: 0 15px 18px;
  }

  /* CTA */
  .pricing-cta {
    padding: 60px 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .pricing-cta-content {
    text-align: center;
    color: white;
  }

  .pricing-cta-content h2 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 25px;
  }

  .pricing-cta-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 26px;
    background: #f8b400;
    color: #fff !important;
    border-radius: 6px;
    text-decoration: none !important;
    font-size: 15px;
    font-weight: 700;
  }

  /* LOADING */
  .pricing-loading {
    min-height: 450px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 15px;
  }

  .pricing-spinner {
    width: 45px;
    height: 45px;
    border: 4px solid #eee;
    border-top-color: #4eac6d;
    border-radius: 50%;
    animation: pricingSpin 0.8s linear infinite;
  }

  @keyframes pricingSpin {
    to { transform: rotate(360deg); }
  }

  /* ERROR */
  .pricing-error {
    min-height: 450px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
  }

  .pricing-error i {
    font-size: 50px;
    color: #dc3545;
  }

  .pricing-error h3 {
    margin-top: 15px;
  }

  /* NO PLANS */
  .no-plans {
    padding: 65px 20px;
    text-align: center;
  }

  .no-plans i {
    font-size: 55px;
    color: #aaa;
  }

  .no-plans h4 {
    margin-top: 15px;
    font-size: 21px;
  }

  .no-plans p {
    color: #777;
  }

  /* RESPONSIVE */
  @media (max-width: 991px) {
    .pricing-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 24px;
    }

    .pricing-heading h1 { font-size: 34px; }
    .price-number { font-size: 46px; }
  }

  @media (max-width: 767px) {
    .pricing-grid {
      grid-template-columns: 1fr;
      grid-auto-rows: auto;
      max-width: 500px;
      margin: auto;
    }

    .pricing-heading h1 { font-size: 30px; }
    .pricing-heading p { font-size: 16px; }

    .dynamic-pricing-card { min-height: auto; }
    .pricing-card-header { min-height: auto; }
    .pricing-price { min-height: auto; padding-bottom: 6px; }
    .pricing-extra { min-height: 0; }

    .pricing-feature { font-size: 14px; }
    .pricing-cta-content h2 { font-size: 27px; }
  }

  @media (max-width: 480px) {
    .dynamic-pricing-section {
      padding-left: 10px;
      padding-right: 10px;
    }

    .pricing-heading h1 { font-size: 27px; }
    .pricing-heading p { font-size: 15px; }

    .pricing-card-header {
      padding-left: 15px;
      padding-right: 15px;
    }

    .pricing-card-header h2 { font-size: 23px; }
    .price-number { font-size: 44px; }
    .pricing-feature { font-size: 14px; }

    .pricing-buy-btn {
      font-size: 14px;
      min-height: 50px;
    }
  }
`}</style>
    </>
  );
};

export default Pricing;